import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { firestore, functions } from "../firebase";
import { CARTOON_NAME_POOL } from "../constants/cartoonNamePool";
import { getActiveEventId, resolveAdminDefaultEventId } from "../utils/activeEvent";
import { faker } from "@faker-js/faker";
import { httpsCallable } from "firebase/functions";
import "./TestData.css";

/**
 * Omit when cloning flyers: identity (replaced with fakes), email history, form-activity,
 * test markers, and timestamps we recreate. Matches sheet import + manifest ticket fields
 * in functions (importFreshlingsFromSheetLogic, importMentorsFromSheetLogic,
 * runEventSummaryFinalize in eventSummaryCore.js, furyRegistrationSync.js).
 */
const FLYER_CLONE_STRIP_KEYS = new Set([
  "id",
  "eventId",
  "email",
  "name",
  "firstName",
  "lastName",
  "properName",
  "nickName",
  "phone",
  "phoneNumber",
  "mobile",
  "emailTemplatesSent",
  "recurrencyUpdate",
  "test",
  "testDataSeed",
  "seededFromEventId",
  "seededAt",
  "seededFromFlyerId",
  "createdAt",
  "manifestTicketsUpdatedAt",
  "furyRegistrationId",
]);

function cloneManifestTickets(mt) {
  if (!mt || typeof mt !== "object") return undefined;
  return { ...mt };
}

/** Copy usage like after manifest sync but reset check-ins (used) for the test event. */
function cloneUsageWithUsedZeroed(usage) {
  if (!usage || typeof usage !== "object") return undefined;
  const out = {};
  for (const [label, val] of Object.entries(usage)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      out[label] = { ...val, used: 0 };
    } else {
      out[label] = val;
    }
  }
  return out;
}

function buildClonedFlyerDoc({ source, targetEventId, sourceEventId, fake }) {
  const cloned = {};
  for (const key of Object.keys(source)) {
    if (FLYER_CLONE_STRIP_KEYS.has(key)) continue;
    if (key === "manifestTickets") {
      const mt = cloneManifestTickets(source.manifestTickets);
      if (mt !== undefined) cloned.manifestTickets = mt;
      continue;
    }
    if (key === "usage") {
      const u = cloneUsageWithUsedZeroed(source.usage);
      if (u !== undefined) cloned.usage = u;
      continue;
    }
    const val = source[key];
    if (val !== undefined) {
      cloned[key] = val;
    }
  }

  const hadPhone =
    (source.phone != null && String(source.phone).trim() !== "") ||
    (source.phoneNumber != null && String(source.phoneNumber).trim() !== "") ||
    (source.mobile != null && String(source.mobile).trim() !== "");

  Object.assign(cloned, {
    eventId: targetEventId,
    email: fake.email,
    name: fake.fullName,
    firstName: fake.firstName,
    lastName: fake.lastName,
    test: true,
    testDataSeed: "event-clone",
    seededFromEventId: sourceEventId,
    seededFromFlyerId: source.id,
    seededAt: new Date().toISOString(),
    createdAt: serverTimestamp(),
  });

  if (source.type === "freshling") {
    cloned.properName = fake.fullName;
    cloned.nickName = fake.firstName;
    cloned.lastName = fake.lastName;
    cloned.phone = fake.phone;
  } else if (hadPhone) {
    cloned.phone = fake.phone;
  }

  return cloned;
}

const TestData = () => {
  // ------------------- STATE -------------------
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [sourceEvent, setSourceEvent] = useState("");

  // Loaded from Firestore for the selected event
  const [teams, setTeams] = useState([]);
  const [bonusPuzzles, setBonusPuzzles] = useState([]);
  // We'll keep references for sky/bonus scores but not strictly required
  const [, setSkyScores] = useState([]);
  const [, setBonusScores] = useState([]);

  // For specifying how many mentors/freshlings/teams to generate
  const [mentorCount, setMentorCount] = useState(5);
  const [freshlingCount, setFreshlingCount] = useState(10);
  const [teamCount, setTeamCount] = useState(5);

  // ------------------- FETCH EVENTS -------------------
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = collection(firestore, "events");
        const snapshot = await getDocs(eventsRef);
        const allEvents = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEvents(allEvents);

        const activeId = await getActiveEventId();
        if (allEvents.length > 0) {
          setSelectedEvent(
            resolveAdminDefaultEventId({
              eventIdParam: "",
              activeEventId: activeId,
              events: allEvents,
            })
          );
        }
      } catch (err) {
        console.error("Error fetching events:", err);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!events.length || !selectedEvent) return;
    const existingSourceIsValid = sourceEvent && events.some((evt) => evt.id === sourceEvent);
    if (existingSourceIsValid && sourceEvent !== selectedEvent) return;
    const fallback = events.find((evt) => evt.id !== selectedEvent);
    setSourceEvent(fallback ? fallback.id : "");
  }, [events, selectedEvent, sourceEvent]);

  // ------------------- FETCH TEAMS WHEN EVENT CHANGES -------------------
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchTeams = async () => {
      try {
        const teamsRef = collection(firestore, "teams");
        const teamsQuery = query(teamsRef, where("eventId", "==", selectedEvent));
        const snapshot = await getDocs(teamsQuery);

        const loadedTeams = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTeams(loadedTeams);
        console.log("Loaded teams for event:", selectedEvent, loadedTeams);
      } catch (err) {
        console.error("Error fetching teams:", err);
      }
    };

    fetchTeams();
  }, [selectedEvent]);

  // ------------------- FETCH BONUS PUZZLES WHEN EVENT CHANGES -------------------
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchBonusPuzzles = async () => {
      try {
        const puzzleRef = collection(firestore, "bonusPuzzles");
        const puzzleQuery = query(puzzleRef, where("eventId", "==", selectedEvent));
        const puzzleSnap = await getDocs(puzzleQuery);

        const puzzlesForEvent = puzzleSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBonusPuzzles(puzzlesForEvent);
        console.log("Loaded bonus puzzles for event:", selectedEvent, puzzlesForEvent);
      } catch (err) {
        console.error("Failed to fetch bonus puzzles:", err);
      }
    };

    fetchBonusPuzzles();
  }, [selectedEvent]);

  // ------------------- HELPER: CHECK EVENT SELECTED -------------------
  const ensureEventSelected = () => {
    if (!selectedEvent) {
      alert("Please select an event first.");
      return false;
    }
    return true;
  };

  const selectedEventTitle = () => {
    const ev = events.find((e) => e.id === selectedEvent);
    return (ev && (ev.title || "").trim()) || selectedEvent || "this event";
  };

  const buildFakeIdentity = (type, index) => {
    const base = CARTOON_NAME_POOL[index % CARTOON_NAME_POOL.length];
    const fullName = `${base} #${index + 1}`;
    const [firstName, ...lastParts] = fullName.split(" ");
    const lastName = lastParts.join(" ");
    const safeEvent = (selectedEvent || "event").toLowerCase().replace(/[^a-z0-9]/g, "");
    const email = `toon-${type}-${safeEvent}-${index + 1}@test.local`;
    const phone = `555-01${String((index % 100) + 10).padStart(2, "0")}`;
    return { fullName, firstName, lastName, email, phone };
  };

  const cloneFlyersFromSourceEvent = async () => {
    if (!ensureEventSelected()) return;
    if (!sourceEvent) {
      alert("Please select a source event.");
      return;
    }
    if (sourceEvent === selectedEvent) {
      alert("Source and target events must be different.");
      return;
    }

    try {
      const sourceQuery = query(
        collection(firestore, "flyers"),
        where("eventId", "==", sourceEvent)
      );
      const sourceSnapshot = await getDocs(sourceQuery);
      const sourceFlyers = sourceSnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((flyer) => flyer.type === "mentor" || flyer.type === "freshling");

      if (sourceFlyers.length === 0) {
        alert("No mentors or freshlings found in the source event.");
        return;
      }

      let created = 0;
      for (let i = 0; i < sourceFlyers.length; i++) {
        const source = sourceFlyers[i];
        const fake = buildFakeIdentity(source.type, i);
        const cloned = buildClonedFlyerDoc({
          source,
          targetEventId: selectedEvent,
          sourceEventId: sourceEvent,
          fake,
        });

        await addDoc(collection(firestore, "flyers"), cloned);
        created += 1;
      }

      alert(`Cloned ${created} mentors/freshlings into the selected event as test data.`);
    } catch (error) {
      console.error("Error cloning flyers:", error);
      alert("Failed to clone flyers: " + (error.message || error));
    }
  };

  const clearClonedFlyers = async () => {
    if (!ensureEventSelected()) return;

    try {
      const flyersQuery = query(
        collection(firestore, "flyers"),
        where("eventId", "==", selectedEvent),
        where("test", "==", true)
      );
      const snapshot = await getDocs(flyersQuery);
      const seededDocs = snapshot.docs.filter(
        (docSnap) => docSnap.data().testDataSeed === "event-clone"
      );
      const n = seededDocs.length;
      const title = selectedEventTitle();
      if (
        !window.confirm(
          `Delete ${n} cloned test flyer(s) for "${title}" only?\n\n` +
            `Only rows created by "Clone Mentors + Freshlings from Source" are removed; other events are not affected.`
        )
      ) {
        return;
      }

      for (const docSnap of seededDocs) {
        await deleteDoc(docSnap.ref);
      }

      alert(`Deleted ${n} cloned test flyers from this event.`);
    } catch (error) {
      console.error("Error clearing cloned flyers:", error);
      alert("Failed to clear cloned flyers: " + (error.message || error));
    }
  };

  // -------------------------------------------------------
  //                      FLYERS: MENTORS
  // -------------------------------------------------------
  const generateMentors = async (count) => {
    try {
      if (!ensureEventSelected()) return;

      for (let i = 0; i < count; i++) {
        const docData = {
          eventId: selectedEvent,
          email: faker.internet.email(),
          name: faker.person.fullName(),
          type: "mentor",
          jumpNumber: "",
        };
        await addDoc(collection(firestore, "flyers"), docData);
      }
      console.log(`Generated ${count} mentors (event: ${selectedEvent}).`);
    } catch (error) {
      console.error("Error generating mentors:", error);
    }
  };

  const clearMentors = async () => {
    try {
      if (!ensureEventSelected()) return;

      const flyersRef = collection(firestore, "flyers");
      const mentorQuery = query(
        flyersRef,
        where("type", "==", "mentor"),
        where("eventId", "==", selectedEvent)
      );
      const mentorSnapshot = await getDocs(mentorQuery);
      const n = mentorSnapshot.size;
      const title = selectedEventTitle();
      const msg =
        `Delete ${n} mentor flyer(s) for "${title}" only?\n\n` +
        `Other events are not affected. This removes every mentor-type flyer for this event (including real registrations), permanently.`;
      if (!window.confirm(msg)) return;

      for (const docSnap of mentorSnapshot.docs) {
        await deleteDoc(docSnap.ref);
      }
      console.log("Mentors cleared for event:", selectedEvent);
    } catch (error) {
      console.error("Error clearing mentors:", error);
    }
  };

  // -------------------------------------------------------
  //                      FLYERS: FRESHLINGS
  // -------------------------------------------------------
  const generateFreshlings = async (count) => {
    try {
      if (!ensureEventSelected()) return;

      for (let i = 0; i < count; i++) {
        const docData = {
          eventId: selectedEvent,
          email: faker.internet.email(),
          name: faker.person.fullName(),
          type: "freshling",
          jumpNumber: faker.number.int({ min: 25, max: 150 }),
        };
        await addDoc(collection(firestore, "flyers"), docData);
      }
      console.log(`Generated ${count} freshlings (event: ${selectedEvent}).`);
    } catch (error) {
      console.error("Error generating freshlings:", error);
    }
  };

  const clearFreshlings = async () => {
    try {
      if (!ensureEventSelected()) return;

      const flyersRef = collection(firestore, "flyers");
      const freshlingQuery = query(
        flyersRef,
        where("type", "==", "freshling"),
        where("eventId", "==", selectedEvent)
      );
      const freshlingSnapshot = await getDocs(freshlingQuery);
      const n = freshlingSnapshot.size;
      const title = selectedEventTitle();
      const msg =
        `Delete ${n} freshling flyer(s) for "${title}" only?\n\n` +
        `Other events are not affected. This removes every freshling-type flyer for this event (including real registrations), permanently.`;
      if (!window.confirm(msg)) return;

      for (const docSnap of freshlingSnapshot.docs) {
        await deleteDoc(docSnap.ref);
      }
      console.log("Freshlings cleared for event:", selectedEvent);
    } catch (error) {
      console.error("Error clearing freshlings:", error);
    }
  };

  // -------------------------------------------------------
  //                      TEAMS
  // -------------------------------------------------------
  // 1 unique mentor + 2 unique freshlings per team, no repeats in one run.
  const generateTeams = async (count) => {
    if (!ensureEventSelected()) return;

    try {
      // 1) Fetch all mentors for this event
      const flyersRef = collection(firestore, "flyers");
      const mentorQuery = query(
        flyersRef,
        where("eventId", "==", selectedEvent),
        where("type", "==", "mentor")
      );
      const mentorSnap = await getDocs(mentorQuery);
      const mentors = mentorSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // 2) Fetch all freshlings for this event
      const freshQuery = query(
        flyersRef,
        where("eventId", "==", selectedEvent),
        where("type", "==", "freshling")
      );
      const freshSnap = await getDocs(freshQuery);
      const freshlings = freshSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // 3) Check we have enough
      if (mentors.length < count) {
        alert(`Not enough mentors for ${count} teams. Found ${mentors.length}.`);
        return;
      }
      if (freshlings.length < count * 2) {
        alert(
          `Not enough freshlings for ${count} teams (need ${2 * count}, have ${freshlings.length}).`
        );
        return;
      }

      // 4) Shuffle arrays to assign randomly
      const shuffle = (arr) => {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
      };
      shuffle(mentors);
      shuffle(freshlings);

      // 5) Create teams: 1 mentor + 2 freshlings per team
      const createdTeams = [];
      for (let i = 0; i < count; i++) {
        const mentorObj = mentors[i];
        // 2 freshlings for this team
        const freshlingsPair = freshlings.slice(i * 2, i * 2 + 2);

        const newTeam = {
          eventId: selectedEvent,
          teamNumber: (i + 1).toString(),
          teamName: faker.company.name(),
          mentor: mentorObj.email,
          division: Math.random() > 0.5 ? "Beginner" : "Intermediate",
          guest: false,
          passcode: faker.word.noun().toLowerCase(),
          freshlings: freshlingsPair.map((fl) => fl.email),
        };

        const docRef = await addDoc(collection(firestore, "teams"), newTeam);
        createdTeams.push({ ...newTeam, id: docRef.id });
      }

      setTeams(createdTeams);
      console.log(`Created ${count} teams for event: ${selectedEvent}`);
    } catch (err) {
      console.error("Error generating teams:", err);
    }
  };

  const clearTeams = async () => {
    try {
      if (!ensureEventSelected()) return;

      const teamsRef = collection(firestore, "teams");
      const teamsQuery = query(teamsRef, where("eventId", "==", selectedEvent));
      const querySnapshot = await getDocs(teamsQuery);
      const n = querySnapshot.size;
      const title = selectedEventTitle();
      if (
        !window.confirm(
          `Delete ${n} team(s) for "${title}" only?\n\nOther events are not affected.`
        )
      ) {
        return;
      }
      for (const docSnap of querySnapshot.docs) {
        await deleteDoc(docSnap.ref);
      }
      setTeams([]);
      console.log("Teams cleared successfully for event:", selectedEvent);
    } catch (error) {
      console.error("Error clearing teams:", error);
    }
  };

  // Update existing teams with generated team names (does not create new teams).
  const generateTeamNamesForExisting = async () => {
    if (!ensureEventSelected()) return;
    if (teams.length === 0) {
      alert("No teams found for this event. Generate teams first or select an event that has teams.");
      return;
    }
    try {
      const used = new Set();
      for (const team of teams) {
        let name = faker.company.name();
        while (used.has(name)) name = faker.company.name();
        used.add(name);
        await updateDoc(doc(firestore, "teams", team.id), { teamName: name });
      }
      console.log(`Generated team names for ${teams.length} teams.`);
      alert(`Updated ${teams.length} teams with new names.`);
      const teamsRef = collection(firestore, "teams");
      const teamsQuery = query(teamsRef, where("eventId", "==", selectedEvent));
      const snapshot = await getDocs(teamsQuery);
      setTeams(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error generating team names:", err);
      alert("Error updating team names: " + (err.message || err));
    }
  };

  // Update existing teams with random divisions (Beginner / Intermediate).
  const generateDivisionsForExisting = async () => {
    if (!ensureEventSelected()) return;
    if (teams.length === 0) {
      alert("No teams found for this event. Generate teams first or select an event that has teams.");
      return;
    }
    try {
      const divisions = ["Beginner", "Intermediate"];
      for (const team of teams) {
        const division = faker.helpers.arrayElement(divisions);
        await updateDoc(doc(firestore, "teams", team.id), { division });
      }
      console.log(`Generated divisions for ${teams.length} teams.`);
      alert(`Updated ${teams.length} teams with random divisions.`);
      const teamsRef = collection(firestore, "teams");
      const teamsQuery = query(teamsRef, where("eventId", "==", selectedEvent));
      const snapshot = await getDocs(teamsQuery);
      setTeams(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error generating divisions:", err);
      alert("Error updating divisions: " + (err.message || err));
    }
  };

  const unpairAllTeamsRefreshLoadAndManifest = async () => {
    if (!ensureEventSelected()) return;
    const confirmed = window.confirm(
      "CAUTION: This will clear mentor/freshling pairings, team numbers, and load order fields for ALL teams in this event, then repopulate Event Day Manifest from those teams. Continue?"
    );
    if (!confirmed) return;

    try {
      const teamsRef = collection(firestore, "teams");
      const teamsQuery = query(teamsRef, where("eventId", "==", selectedEvent));
      const teamsSnapshot = await getDocs(teamsQuery);

      for (const docSnap of teamsSnapshot.docs) {
        await updateDoc(docSnap.ref, {
          mentor: "",
          freshlings: [],
          teamNumber: null,
          planeId: null,
          planeLoadIndex: null,
          slotIndexInLoad: null,
        });
      }

      const populateManifest = httpsCallable(functions, "populateEventManifest");
      await populateManifest({ eventId: selectedEvent });

      const refreshedTeamsSnapshot = await getDocs(teamsQuery);
      setTeams(refreshedTeamsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      alert(
        `Reset ${teamsSnapshot.size} team(s): unpaired everyone, cleared load-order fields, and refreshed Event Day Manifest.`
      );
    } catch (error) {
      console.error("Error resetting team pairings/load order/manifest:", error);
      alert("Failed to reset pairings/load order/manifest: " + (error.message || error));
    }
  };

  // -------------------------------------------------------
  //                    SKY SCORES
  // -------------------------------------------------------
  // Matches your SkyScoreAdmin approach:
  // doc ID => `${teamId}_sky_${round}`
  // fields => { eventId, teamId, type: "sky", round, raw, busts, value }
  const generateSkyScores = async () => {
    try {
      if (!ensureEventSelected()) return;

      const generated = [];
      // We'll do 4 rounds per team
      for (const team of teams) {
        if (team.eventId !== selectedEvent) continue;

        for (let round = 1; round <= 4; round++) {
          // Random raw 1..12, random bust 0..3
          const raw = faker.number.int({ min: 1, max: 12 });
          const busts = faker.number.int({ min: 0, max: 3 });
          // In your admin UI, total = Math.max(0, raw - busts)
          const total = Math.max(0, raw - busts);

          const scoreDoc = {
            eventId: selectedEvent,
            teamId: team.id,
            type: "sky",
            round,
            raw,
            busts,
            value: total,
          };

          // Use a known doc ID: "TEAMID_sky_ROUND"
          const docId = `${team.id}_sky_${round}`;
          await setDoc(doc(firestore, "scores", docId), scoreDoc);
          generated.push(scoreDoc);
        }
      }
      setSkyScores(generated);
      console.log("Sky scores generated using doc ID format for event:", selectedEvent);
    } catch (error) {
      console.error("Error generating sky scores:", error);
    }
  };

  const clearSkyScores = async () => {
    try {
      if (!ensureEventSelected()) return;

      const scoresRef = collection(firestore, "scores");
      // typically your sky scores also have type == "sky", but
      // if you want to remove all "scores" for the event, that's fine
      const scoresQuery = query(scoresRef, where("eventId", "==", selectedEvent));
      const querySnapshot = await getDocs(scoresQuery);
      const n = querySnapshot.size;
      const title = selectedEventTitle();
      if (
        !window.confirm(
          `Delete ${n} score document(s) for "${title}" only?\n\nOther events are not affected.`
        )
      ) {
        return;
      }
      for (const docSnap of querySnapshot.docs) {
        await deleteDoc(docSnap.ref);
      }
      setSkyScores([]);
      console.log("Sky scores cleared for event:", selectedEvent);
    } catch (error) {
      console.error("Error clearing sky scores:", error);
    }
  };

  // -------------------------------------------------------
  //                   BONUS SCORES
  // -------------------------------------------------------
  const generateBonusScores = async () => {
    try {
      if (!ensureEventSelected()) return;

      const eventTeams = teams.filter((t) => t.eventId === selectedEvent);
      const bonusScoresData = [];

      // Example: not every team does every puzzle
      for (const puzzle of bonusPuzzles) {
        // random subset approach, or if you want all, do eventTeams
        const howMany = faker.number.int({ min: 0, max: eventTeams.length });
        const shuffledTeams = [...eventTeams];
        for (let i = shuffledTeams.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffledTeams[i], shuffledTeams[j]] = [shuffledTeams[j], shuffledTeams[i]];
        }
        const chosen = shuffledTeams.slice(0, howMany);

        for (const team of chosen) {
          const scoreDoc = {
            eventId: selectedEvent,
            teamId: team.id,
            puzzleId: puzzle.id,
            value: Number(puzzle.value),
            timestamp: new Date(),
          };
          await addDoc(collection(firestore, "bonusScores"), scoreDoc);
          bonusScoresData.push(scoreDoc);
        }
      }
      setBonusScores(bonusScoresData);
      console.log("Bonus scores generated for event:", selectedEvent);
    } catch (error) {
      console.error("Error generating bonus scores:", error);
    }
  };

  const clearBonusScores = async () => {
    try {
      if (!ensureEventSelected()) return;

      const bonusScoresRef = collection(firestore, "bonusScores");
      const bonusScoresQuery = query(bonusScoresRef, where("eventId", "==", selectedEvent));
      const querySnapshot = await getDocs(bonusScoresQuery);
      const n = querySnapshot.size;
      const title = selectedEventTitle();
      if (
        !window.confirm(
          `Delete ${n} bonus score row(s) for "${title}" only?\n\nOther events are not affected.`
        )
      ) {
        return;
      }
      for (const docSnap of querySnapshot.docs) {
        await deleteDoc(docSnap.ref);
      }
      setBonusScores([]);
      console.log("Bonus scores cleared for event:", selectedEvent);
    } catch (error) {
      console.error("Error clearing bonus scores:", error);
    }
  };

  // -------------------------------------------------------
  //                     RENDER
  // -------------------------------------------------------
  return (
    <div className="test-data-container">
      <h2>Test Data Management</h2>

      {/* -------------- EVENT DROPDOWN -------------- */}
      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor="eventSelect">Select Target Event:</label>{" "}
        <select
          id="eventSelect"
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          style={{ padding: "0.5rem" }}
        >
          <option value="">-- Select an event --</option>
          {events.map((evt) => (
            <option key={evt.id} value={evt.id}>
              {evt.title || `Event ${evt.id}`}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor="sourceEventSelect">Select Source Event:</label>{" "}
        <select
          id="sourceEventSelect"
          value={sourceEvent}
          onChange={(e) => setSourceEvent(e.target.value)}
          style={{ padding: "0.5rem" }}
        >
          <option value="">-- Select a source event --</option>
          {events
            .filter((evt) => evt.id !== selectedEvent)
            .map((evt) => (
              <option key={evt.id} value={evt.id}>
                {evt.title || `Event ${evt.id}`}
              </option>
            ))}
        </select>
      </div>

      <div className="test-data-buttons">
        <button onClick={cloneFlyersFromSourceEvent}>Clone Mentors + Freshlings from Source</button>
        <button onClick={clearClonedFlyers}>Clear Cloned Test Flyers</button>
      </div>

      {/* -------------- MENTOR/FRESHLING/TEAM COUNT INPUTS -------------- */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <div>
          <label>Mentor Count: </label>
          <input
            type="number"
            value={mentorCount}
            onChange={(e) => setMentorCount(Number(e.target.value))}
            style={{ width: "5rem" }}
          />
        </div>
        <div>
          <label>Freshling Count: </label>
          <input
            type="number"
            value={freshlingCount}
            onChange={(e) => setFreshlingCount(Number(e.target.value))}
            style={{ width: "5rem" }}
          />
        </div>
        <div>
          <label>Team Count: </label>
          <input
            type="number"
            value={teamCount}
            onChange={(e) => setTeamCount(Number(e.target.value))}
            style={{ width: "5rem" }}
          />
        </div>
      </div>

      {/* -------------- MENTORS BUTTONS -------------- */}
      <div className="test-data-buttons">
        <button onClick={() => generateMentors(mentorCount)}>Generate Mentors</button>
        <button onClick={clearMentors}>Clear Mentors</button>
      </div>

      {/* -------------- FRESHLINGS BUTTONS -------------- */}
      <div className="test-data-buttons">
        <button onClick={() => generateFreshlings(freshlingCount)}>Generate Freshlings</button>
        <button onClick={clearFreshlings}>Clear Freshlings</button>
      </div>

      {/* -------------- TEAMS BUTTONS -------------- */}
      <div className="test-data-buttons">
        <button onClick={() => generateTeams(teamCount)}>Generate Teams</button>
        <button onClick={clearTeams}>Clear Teams</button>
        <button onClick={generateTeamNamesForExisting}>Generate team names (existing)</button>
        <button onClick={generateDivisionsForExisting}>Generate divisions (existing)</button>
        <button onClick={unpairAllTeamsRefreshLoadAndManifest}>
          CAUTION: Unpair all + refresh load/manifest
        </button>
      </div>

      {/* -------------- SKY SCORES -------------- */}
      <div className="test-data-buttons">
        <button onClick={generateSkyScores}>Generate Sky Scores</button>
        <button onClick={clearSkyScores}>Clear Sky Scores</button>
      </div>

      {/* -------------- BONUS SCORES -------------- */}
      <div className="test-data-buttons">
        <button onClick={generateBonusScores}>Generate Bonus Scores</button>
        <button onClick={clearBonusScores}>Clear Bonus Scores</button>
      </div>
    </div>
  );
};

export default TestData;
